import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Music2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { API } from "@/lib/api";

const FEATURED_TITLES = [
  "the rise of ethan brooks",
  "grace bennett's fireside christmas",
  "michael reed: thirty years of power",
];

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const fmtTime = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export default function GalleryShowcase() {
  const { language } = useI18n();
  const isEn = language === "en";
  const [songs, setSongs] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [progress, setProgress] = useState({ current: 0, duration: 0 });
  const audioRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/public/gallery?limit=60`)
      .then((r) => r.json())
      .then((all) => {
        if (!Array.isArray(all)) return;
        const picked = FEATURED_TITLES
          .map((t) => all.find((s) => (s.title || "").toLowerCase() === t))
          .filter(Boolean);
        const fallback = all.filter((s) => s.has_thumbnail && !picked.includes(s));
        setSongs([...picked, ...fallback].slice(0, 3));
      })
      .catch(() => {});
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, []);

  const togglePlay = (token) => {
    if (playing === token) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(`${API}/public/song/${token}/audio`);
    audio.ontimeupdate = () => setProgress({ current: audio.currentTime, duration: audio.duration || 0 });
    audio.onended = () => setPlaying(null);
    audio.play().catch(() => {});
    audioRef.current = audio;
    setProgress({ current: 0, duration: 0 });
    setPlaying(token);
  };

  const seek = (e, token) => {
    if (playing !== token || !audioRef.current || !progress.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = ratio * progress.duration;
  };

  if (!songs.length) return null;

  return (
    <section className="px-4 pt-4 pb-8 sm:px-6 lg:px-8" data-testid="gallery-showcase-section">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            {isEn ? "Real creations" : "Vraies créations"}
          </p>
          <h2 className="mt-2 font-heading text-2xl font-medium tracking-tight sm:text-3xl">
            {isEn ? "Listen to songs created with Kalypso Music" : "Écoutez des chansons créées avec Kalypso Music"}
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          {songs.map((song, i) => {
            const isPlaying = playing === song.share_token;
            return (
              <motion.div
                key={song.share_token}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`group overflow-hidden rounded-2xl border bg-card transition-all hover:-translate-y-1 ${isPlaying ? "border-primary/60 shadow-[0_0_30px_-10px_rgba(168,85,247,0.5)]" : "border-border"}`}
                data-testid={`showcase-song-${i + 1}`}
              >
                <div className="relative aspect-square cursor-pointer overflow-hidden bg-muted" onClick={() => togglePlay(song.share_token)}>
                  {song.has_thumbnail ? (
                    <img
                      src={`${API}/public/song/${song.share_token}/thumbnail`}
                      alt={song.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center"><Music2 className="h-10 w-10 text-muted-foreground" /></div>
                  )}
                  {isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
                        <Pause className="h-6 w-6" />
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="truncate font-heading text-sm font-semibold">{song.title}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">{cap(song.style)}{song.mood ? ` · ${cap(song.mood)}` : ""}</p>
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      onClick={() => togglePlay(song.share_token)}
                      data-testid={`showcase-play-${i + 1}`}
                      aria-label={isPlaying ? "Pause" : "Play"}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow transition-transform hover:scale-105"
                    >
                      {isPlaying ? <Pause className="h-4.5 w-4.5" /> : <Play className="ml-0.5 h-4.5 w-4.5" />}
                    </button>
                    <div className="flex-1">
                      <div
                        className="h-2 w-full cursor-pointer overflow-hidden rounded-full bg-muted"
                        onClick={(e) => seek(e, song.share_token)}
                        data-testid={`showcase-progress-${i + 1}`}
                      >
                        <div
                          className="h-full rounded-full bg-primary transition-[width] duration-300"
                          style={{ width: isPlaying && progress.duration ? `${(progress.current / progress.duration) * 100}%` : "0%" }}
                        />
                      </div>
                      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                        <span>{isPlaying ? fmtTime(progress.current) : "0:00"}</span>
                        <span>{isPlaying && progress.duration ? fmtTime(progress.duration) : "▶ MP3"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
